import { wktToGeoJSON } from '@terraformer/wkt';
import { has } from 'lodash';
import { proxy } from 'valtio';
import { proxySet } from 'valtio/utils';
import { getProject, parcelPointQuery } from '~/api/projects2';
import { OrderStep } from '~/pages/projectDetails/landRights/types';

export interface ParcelDetails {
  i: number;
  address: string;
  geom: GeoJSON.Polygon;
  uniqueId: string;
  titleJson?: any;
  parcelId: string;
  censusZip?: string;
  mailZipcode?: string;
  latitude?: string;
  longitude?: string;
  owner?: string;
  physzip?: string;
  yearBuilt?: number;
}

function transformParcelDetails(parcelDetails: any[]) {
  let counter = 0;
  const parcelMap: { [key: string]: number } = {};

  return parcelDetails
    .filter((parcel: any) => parcel.geomAsWkt)
    .map((parcel: any) => {
      // this helps handling condos where multiple units have same parcelId
      if (has(parcelMap, parcel.parcelId)) {
        // nothing to do, already exists
      } else {
        counter = counter + 1;
        parcelMap[parcel.parcelId] = counter;
      }
      parcel.i = parcelMap[parcel.parcelId];

      parcel.address = `${parcel.addrNumber || ''} ${
        parcel.addrStreetName || ''
      } ${parcel.addrStreetType || ''}, ${parcel.physcity || ''} ${parcel.stateAbbr || ''} ${parcel.physzip || ''}`;
      parcel.geom = wktToGeoJSON(parcel.geomAsWkt);
      parcel.uniqueId = `${parcel.robustId}.${parcel.parcelId}`;
      return parcel;
    })
    .sort((a: ParcelDetails, b: ParcelDetails) => a.i - b.i);
}

class Store {
  selectedIds: Set<string> = proxySet();
  parcelDetails: ParcelDetails[] = [];
  notAvailableParcels: string[] = [];
  parcelsAvailableToDownload: string[] = [];
  vendorFailedParcels: string[] = [];
  failedParcels: string[] = [];
  orderedParcelCount = 0;
  documentOrderStep: OrderStep = OrderStep.Confirm;

  async fetchParcelDetails(projectId: string) {
    this.selectedIds.clear();
    const p = await getProject(projectId);
    this.parcelDetails = transformParcelDetails(p.parcelDetails);
  }

  async fetchParcelForPoint(projectId: string, lng: number, lat: number) {
    const { parcelDetails, parcelDataAdds } = await parcelPointQuery(projectId, lng, lat);
    this.parcelDetails = transformParcelDetails(parcelDetails);
    transformParcelDetails(parcelDataAdds).forEach((p) => {
      this.selectedIds.add(p.uniqueId);
    });
  }

  updatenNotAvailableParcels(parcelIds: string[]) {
    this.notAvailableParcels = parcelIds;
  }

  updateOrderSummary(summary: {
    parcelsAvailableToDownload: string[];
    vendorFailedParcels: string[];
    failedParcels: string[];
    orderedParcelCount: number;
  }) {
    this.parcelsAvailableToDownload = summary.parcelsAvailableToDownload;
    this.vendorFailedParcels = summary.vendorFailedParcels;
    this.failedParcels = summary.failedParcels;
    this.orderedParcelCount = summary.orderedParcelCount;
  }

  updateDocumentOrderStep(step: OrderStep) {
    this.documentOrderStep = step;
  }

  resetOrderSummary() {
    this.notAvailableParcels = [];
    this.parcelsAvailableToDownload = [];
    this.vendorFailedParcels = [];
    this.failedParcels = [];
    this.orderedParcelCount = 0;
  }
}

const store = proxy(new Store());

export default store;
